// src/layouts/commit/reviewComponents/DiffRenderer.js

import React from 'react';
import { Grid, Divider } from '@mui/material';
import { grey, red, green } from '@mui/material/colors';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import { useVisibility } from 'context/visibilityContext';
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter';

/**
 * A robust formatter that can safely render any data type by converting
 * non-primitive values to a pretty-printed JSON string.
 */
const formatValue = (value) => {
  // Handle null or undefined explicitly
  if (value === null || value === undefined) {
    return '-';
  }
  // Handle primitive types directly
  const type = typeof value;
  if (type === 'string' || type === 'number' || type === 'boolean') {
    return value.toString();
  }
  // For objects and arrays, use JSON.stringify with pretty-printing
  if (type === 'object') {
    try {
      // The `null, 2` arguments add indentation for readability
      return JSON.stringify(value, null, 2);
    } catch (e) {
      // Fallback for circular references or other stringify errors
      return '[Unserializable Object]';
    }
  }
  // Fallback for any other unexpected type
  return String(value);
};

/**
 * A reusable component to render the diff array for any sObject,
 * respecting the visibility configuration.
 */
function DiffRenderer({ changes, sObjectName, orgType, showSameFields }) {
  const { visibilityConfig, isLoadingConfig } = useVisibility();

  // Wait for the config to load before attempting to filter
  if (isLoadingConfig) {
    return (
      <ArgonTypography variant="caption" color="textSecondary" sx={{ p: 1 }}>
        Loading visibility configuration...
      </ArgonTypography>
    );
  }

  // --- VISIBILITY CONFIG IS NOW USED HERE ---
  const columnFilteredChanges = filterDiffsByDisplayColumns(changes, sObjectName, visibilityConfig, orgType);

  const visibleChanges = columnFilteredChanges?.filter((change) => change.type !== 'same' || showSameFields);

  if (!visibleChanges || visibleChanges.length === 0) {
    return (
      <ArgonTypography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', p: 1 }}>
        No differences to show {showSameFields ? '' : '(excluding "same" fields)'}.
      </ArgonTypography>
    );
  }

  return visibleChanges.map((change, index) => {
    let oldBgColor, newBgColor;

    switch (change.type) {
      case 'new':
        oldBgColor = grey[200];
        newBgColor = green[100];
        break;
      case 'modified':
        oldBgColor = red[100];
        newBgColor = green[100];
        break;
      case 'deleted':
        oldBgColor = red[100];
        newBgColor = grey[200];
        break;
      case 'same':
        oldBgColor = grey[200];
        newBgColor = grey[200];
        break;
      default:
        oldBgColor = grey[200];
        newBgColor = grey[200];
    }

    const oldValueFormatted = formatValue(change.oldValue);
    const newValueFormatted = formatValue(change.newValue);

    return (
      <ArgonBox color="text" key={`${change.field}-${index}`} mb={1} width="100%">
        <Grid container alignItems="center" spacing={0}>
          <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <ArgonTypography variant="caption" fontWeight="bold" align="left" sx={{ wordWrap: 'break-word' }}>
              {change.field}
            </ArgonTypography>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'center' }}>
            <Divider orientation="vertical" flexItem />
          </Grid>
          <Grid item xs={3.5} style={{ textAlign: 'center', padding: '0 4px' }}>
            <ArgonBox bgcolor={oldBgColor} p={1} borderRadius="md" sx={{ height: '100%' }}>
              {/* Use a <pre> tag to respect the formatting from JSON.stringify */}
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.75rem' }}>
                <code>{oldValueFormatted}</code>
              </pre>
            </ArgonBox>
          </Grid>
          <Grid item xs={1} style={{ textAlign: 'center' }}>
            <Divider orientation="vertical" flexItem />
          </Grid>
          <Grid item xs={3.5} style={{ textAlign: 'center', padding: '0 4px' }}>
            <ArgonBox bgcolor={newBgColor} p={1} borderRadius="md" sx={{ height: '100%' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.75rem' }}>
                <code>{newValueFormatted}</code>
              </pre>
            </ArgonBox>
          </Grid>
        </Grid>
      </ArgonBox>
    );
  });
}

export default DiffRenderer;
